import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashWelcome from '../components/DashWelcome';
import { Button } from '../components/Button';
import TabNavigation from '../components/TabNavigation';
import useSectionLayout from '../hooks/useSectionLayout';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';
import VisionSection from '../components/VisionSection';
import WelcomeSection from '../components/WelcomeSection';
import ValuesSection from '../components/ValuesSection';
import GoldFileSection from '../components/GoldFileSection';
import GrowthOpportunitiesSection from '../components/GrowthOpportunitiesSection';
import GoalsSection from '../components/GoalsSection';
import CommunitySection from '../components/CommunitySection';
import { AIPromptSection } from '../components/AIPromptSection';
import LessonsSection from '../components/LessonsSection';

export function DashGrowthLoop() {
  const navigate = useNavigate();
  const { locations } = useSectionLayout();

  const [openSections, setOpenSections] = useState({
    gold_file: false,
    growth_opportunities: false,
    goals: false,
    community: false,
    vision: false,
    lessons: false
  });

  const [showBanner, setShowBanner] = useState(false);
  const [profile, setProfile] = useState<{
    core_value_1: string | null;
    core_value_2: string | null;
    core_value_3: string | null;
    challenge_purchased_at: string | null;
  } | null>(null);

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const loadProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data } = await supabase
        .from('profiles')
        .select('core_value_1, core_value_2, core_value_3, challenge_purchased_at')
        .eq('id', session.user.id)
        .maybeSingle();

      if (data) {
        setProfile(data);
        if (!data.core_value_1 || !data.core_value_2 || !data.core_value_3) {
          setShowBanner(true);
        }
      }
    };

    loadProfile();
  }, []);

  const hasValues = !!(profile?.core_value_1 && profile?.core_value_2 && profile?.core_value_3);
  const hasPurchased = !!profile?.challenge_purchased_at;

  const showGoldFile = locations?.gold_file === 'loop';
  const showGrowthOpportunities = locations?.growth_opportunities === 'loop';
  const showGoals = locations?.goals === 'loop';
  const showCommunity = locations?.community === 'loop';
  const showVision = locations?.vision === 'loop';
  const showLessons = locations?.lessons === 'loop';

  return (
    <ProtectedLayout>
      <div className="p-4 max-w-4xl mx-auto space-y-6 relative">
        <DashWelcome />

        {showBanner && (
          <div className="bg-yellow-100 text-yellow-900 p-4 rounded-lg shadow border border-yellow-300 mb-4">
            <div className="flex items-center justify-between">
              <p className="text-sm">
                Please complete your onboarding to unlock the full experience.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/impactintentions')}
              >
                Finish Onboarding
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-6 mt-6">
          {/* Show ValuesSection when user has values (purchased or not) */}
          {hasValues && (
            <ValuesSection
              value1={profile!.core_value_1!}
              value2={profile!.core_value_2!}
              value3={profile!.core_value_3!}
              hasPurchased={hasPurchased}
            />
          )}

          <WelcomeSection hasPurchased={hasPurchased} />

          {showVision && (
            <VisionSection
              open={openSections.vision}
              toggle={toggleSection}
              sectionKey="vision"
            />
          )}
          {showGoldFile && (
            <GoldFileSection
              open={openSections.gold_file}
              toggle={toggleSection}
            />
          )}
          {showGrowthOpportunities && (
            <GrowthOpportunitiesSection
              open={openSections.growth_opportunities}
              toggle={toggleSection}
              sectionKey="growth_opportunities"
              progress={{}}
              toggleItem={() => {}}
            />
          )}
          {showGoals && (
            <GoalsSection
              open={openSections.goals}
              toggle={toggleSection}
              sectionKey="goals"
            />
          )}
          {showLessons && (
            <LessonsSection
              open={openSections.lessons}
              toggle={toggleSection}
              sectionKey="lessons"
            />
          )}
          <AIPromptSection />
          {showCommunity && (
            <CommunitySection
              open={openSections.community}
              toggle={toggleSection}
              sectionKey="community"
            />
          )}
        </div>

        <TabNavigation />
      </div>
    </ProtectedLayout>
  );
}
